<template>
  <div>
    <b-modal
      ref="apply-voucher-modal"
      hide-footer
      :titleHtml="modalTitle"
      size="xl"
    >
      <v-form ref="form">
        <b-row class="mb-4">
          <b-col md="5">
            <b-input
              size="sm"
              placeholder="Nhập mã voucher hoặc tên chương trình"
              v-model="searchText"
              append-icon="search"
              single-line
              hide-details
            ></b-input>
          </b-col>
        </b-row>

        <!-- E-007: không lấy được dữ liệu nhóm thì vẫn bán được, chỉ nhắc một dòng -->
        <b-alert :show="isGroupUnavailable" variant="light-warning" class="py-2">
          Tạm thời chưa nhóm được voucher, danh sách đang hiển thị đầy đủ.
        </b-alert>
        <b-form-text
          v-if="isGroupedMode && !searchText && !isGroupUnavailable"
          class="mb-3 d-block"
        >
          Ô tìm kiếm quét mọi nhóm, kể cả nhóm đang gập. Kết quả hiện kèm tên
          nhóm chứa nó.
        </b-form-text>

        <!-- Dạng gập mở theo nhóm: chỉ khi bật gom nhóm, không có sự cố, và không tìm kiếm -->
        <template v-if="isGroupedMode && !searchText && !isGroupUnavailable">
          <div v-if="!groupedBuckets.length" class="text-center py-4">
            <h5>Không có voucher nào dùng được cho đơn này</h5>
          </div>
          <div
            v-for="bucket in groupedBuckets"
            :key="bucket.key"
            class="voucher-group mb-2"
          >
            <div class="voucher-group-head" @click="toggleBucket(bucket.key)">
              <i
                :class="[
                  'ki mr-2',
                  openBuckets.includes(bucket.key)
                    ? 'ki-arrow-down'
                    : 'ki-arrow-next',
                ]"
                style="font-size: 0.7rem"
              ></i>
              <span class="font-weight-bold">{{ bucket.name }}</span>
              <span class="label label-inline label-light ml-2"
                >{{ bucket.rows.length }} mã</span
              >
            </div>
            <div v-show="openBuckets.includes(bucket.key)" class="pt-2">
              <b-form-text v-if="bucket.key === UNGROUPED_KEY" class="d-block mb-2">
                Gồm chương trình chưa xếp nhóm và chương trình thuộc nhóm đang
                khóa.
              </b-form-text>
              <b-table
                :items="bucket.rows"
                :fields="fields"
                bordered
                hover
                small
              >
                <template v-slot:cell(voucherValue)="row">
                  <VoucherValueCell :item="row.item" />
                </template>
                <template v-slot:cell(apply)="row">
                  <div
                    class="text-center btn"
                    @click="applyVoucherItem(row.item)"
                  >
                    <i
                      class="fa fa-arrow-circle-down text-success"
                      v-b-tooltip.hover
                      title="Áp dụng"
                    ></i>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </template>

        <!-- Dạng phẳng: đang tìm kiếm, hoặc cờ tắt, hoặc có sự cố dữ liệu nhóm -->
        <b-table
          v-else
          :items="flatRows"
          :fields="flatFields"
          bordered
          hover
          show-empty
        >
          <template #empty>
            <h5 class="text-center">Không có voucher nào được gợi ý</h5>
          </template>
          <template v-slot:cell(groupName)="row">
            <span v-if="row.item.groupName">{{ row.item.groupName }}</span>
            <span v-else class="label label-inline label-light"
              >Chưa phân nhóm</span
            >
          </template>
          <template v-slot:cell(voucherValue)="row">
            <VoucherValueCell :item="row.item" />
          </template>
          <template v-slot:cell(apply)="row">
            <div class="text-center btn" @click="applyVoucherItem(row.item)">
              <i
                class="fa fa-arrow-circle-down text-success"
                v-b-tooltip.hover
                title="Áp dụng"
              ></i>
            </div>
          </template>
        </b-table>

        <b-button
          style="font-weight: 600; width: 70px"
          variant="secondary"
          size="sm"
          @click="hideModal"
        >
          Hủy
        </b-button>
      </v-form>
    </b-modal>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import {
  convertPrice,
  makeToastFaile,
  getApiErrorMessage,
} from '@/utils/common';
import { DISCOUNT_AMOUNT_TYPE_NAME } from '@/utils/enum-name';
import { DISCOUNT_AMOUNT_TYPE } from '@/utils/enum';
import VoucherValueCell from '@/view/components/voucher/VoucherValueCell.vue';

/** Khóa của rổ gom chương trình chưa có nhóm và chương trình thuộc nhóm đang khóa. */
const UNGROUPED_KEY = 'ungrouped';

export default {
  props: [
    'billItemId',
    'productName',
    'storeId',
    'productId',
    'productAmount',
    'customerMobile',
  ],
  components: { VoucherValueCell },
  data() {
    return {
      UNGROUPED_KEY,
      modalTitle: '',
      searchText: '',
      // Mỗi phần tử = 1 MÃ voucher, đã trộn thông tin chương trình + nhóm
      rows: [],
      // API trả dạng gom theo chương trình hay dạng phẳng cũ (cờ tắt)
      isGroupedMode: false,
      isGroupUnavailable: false,
      openBuckets: [],
      fields: [
        {
          key: 'voucherCode',
          label: 'Mã voucher',
          thStyle: { fontWeight: 600, color: '#181c32', width: '22%' },
        },
        {
          key: 'voucherName',
          label: 'Tên chương trình',
          thStyle: { fontWeight: 600, color: '#181c32', width: '38%' },
        },
        {
          key: 'voucherValue',
          label: 'Giá trị',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
          tdClass: 'text-right',
        },
        {
          key: 'usable',
          label: 'Lượt còn lại',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
          formatter: (value) => convertPrice(value),
          tdClass: 'text-right',
        },
        {
          key: 'apply',
          label: 'Áp dụng',
          thStyle: {
            fontWeight: 600,
            color: '#181c32',
            width: '10%',
            textAlign: 'center',
          },
          tdClass: 'text-center',
        },
      ],
      DISCOUNT_AMOUNT_TYPE_NAME,
      DISCOUNT_AMOUNT_TYPE,
    };
  },
  computed: {
    /** Dạng phẳng có thêm cột Nhóm, nhưng chỉ khi đang bật gom nhóm (FR-012c). */
    flatFields() {
      if (!this.isGroupedMode) {
        return this.fields;
      }
      const cloned = [...this.fields];
      cloned.splice(2, 0, {
        key: 'groupName',
        label: 'Nhóm',
        thStyle: { fontWeight: 600, color: '#181c32', width: '18%' },
      });
      return cloned;
    },
    /**
     * Tìm theo CẢ mã voucher lẫn tên chương trình, KHÔNG cắt bớt kết quả (FR-012a, b).
     * Bản cũ chỉ khớp mã và cắt 10 dòng — cắt 10 sẽ giấu mất kết quả ở nhóm khác.
     */
    flatRows() {
      const keyword = (this.searchText || '').trim().toLowerCase();
      if (!keyword) {
        return this.rows;
      }
      return this.rows.filter(
        (row) =>
          String(row.voucherCode || '')
            .toLowerCase()
            .includes(keyword) ||
          String(row.voucherName || '')
            .toLowerCase()
            .includes(keyword),
      );
    },
    /** Gom theo nhóm, rổ "Chưa phân nhóm" luôn xếp cuối. Nhóm rỗng tự biến mất (E-008). */
    groupedBuckets() {
      const byKey = new Map();
      this.rows.forEach((row) => {
        const key = row.groupId || UNGROUPED_KEY;
        if (!byKey.has(key)) {
          byKey.set(key, {
            key,
            name: row.groupName || 'Chưa phân nhóm',
            rows: [],
          });
        }
        byKey.get(key).rows.push(row);
      });
      const buckets = [...byKey.values()];
      return [
        ...buckets.filter((bucket) => bucket.key !== UNGROUPED_KEY),
        ...buckets.filter((bucket) => bucket.key === UNGROUPED_KEY),
      ];
    },
  },
  methods: {
    convertPrice,
    toggleBucket(key) {
      this.openBuckets = this.openBuckets.includes(key)
        ? this.openBuckets.filter((item) => item !== key)
        : [...this.openBuckets, key];
    },
    /**
     * API trả một trong hai dạng:
     *  - gom theo chương trình (có `codes[]`) khi cờ gom nhóm đang bật
     *  - phẳng theo từng mã khi cờ tắt — y hệt trước khi có tính năng
     * Cả hai đều quy về cùng một danh sách dòng để phần còn lại của màn dùng chung.
     */
    normalizeResponse(listData) {
      const isGrouped = listData.some((item) => Array.isArray(item.codes));
      this.isGroupedMode = isGrouped;
      this.isGroupUnavailable = listData.some(
        (item) => item.voucherGroupUnavailable,
      );
      if (!isGrouped) {
        return listData;
      }
      return listData.flatMap((voucher) =>
        voucher.codes.map((code) => ({
          voucherId: voucher.voucherId,
          voucherDetailId: code.voucherDetailId,
          voucherCode: code.voucherCode,
          voucherName: voucher.voucherName,
          usable: code.usable,
          type: voucher.type,
          value: voucher.value,
          maxValue: voucher.maxValue,
          voucherValue: voucher.voucherValue,
          cumluativeValues: voucher.cumluativeValues,
          groupId: voucher.voucherGroup ? voucher.voucherGroup.id : null,
          groupName: voucher.voucherGroup ? voucher.voucherGroup.name : null,
        })),
      );
    },
    async fetchVoucherUsable() {
      const params = {
        productId: this.productId,
        productAmount: this.productAmount,
        storeId: this.storeId,
        customerPhone: this.customerMobile,
        // Server quyết định có gom nhóm hay không dựa trên cờ tính năng; cờ tắt thì
        // param này bị bỏ qua và response về đúng dạng phẳng cũ.
        groupBy: 'voucher',
      };
      // KHÔNG để lỗi thoát ra ngoài: `showModal` await hàm này rồi mới gọi `.show()`,
      // và cả 4 nơi gọi đều fire-and-forget không bắt lỗi. Reject ở đây sẽ làm nút
      // "Áp dụng voucher" bấm không ra gì, không báo gì — dead-end im lặng giữa ca bán.
      try {
        const { data } = await ApiService.query('v2/vouchers/usable', { params });
        this.rows = this.normalizeResponse(data.data || []);
      } catch (err) {
        this.rows = [];
        this.isGroupedMode = false;
        this.isGroupUnavailable = false;
        makeToastFaile(getApiErrorMessage(err, 'Không lấy được danh sách voucher!'));
      }
      // Mở sẵn nhóm đầu tiên để NVBH chọn được mã trong hai thao tác (NFR-002)
      const [firstBucket] = this.groupedBuckets;
      this.openBuckets = firstBucket ? [firstBucket.key] : [];
    },
    showModal: async function () {
      this.searchText = '';
      this.modalTitle = `<div>
              <span class='d-block' style='font-size: 1.1rem'>Áp dụng voucher cho sản phẩm: <a class='text-primary'>#${this.productName}</a></span>
              </div>`;
      await this.fetchVoucherUsable();
      this.$refs['apply-voucher-modal'].show();
    },
    hideModal: function () {
      this.$refs['apply-voucher-modal'].hide();
    },
    applyVoucherItem(voucherItem) {
      const response = {
        voucherId: voucherItem.voucherId,
        voucherDetailId: voucherItem.voucherDetailId,
        billItemId: this.billItemId,
        voucherCode: voucherItem.voucherCode,
        voucherValue: voucherItem.voucherValue,
        cumluativeValues: voucherItem.cumluativeValues,
        voucherType: voucherItem.type,
      };
      this.$emit('applyVoucherItem', response);
      this.hideModal();
    },
  },
};
</script>

<style scoped>
.voucher-group {
  border: 1px solid #ebedf3;
  border-radius: 4px;
}
.voucher-group-head {
  padding: 0.6rem 0.9rem;
  cursor: pointer;
  background: #f3f6f9;
  display: flex;
  align-items: center;
}
.voucher-group-head:hover {
  background: #ecf0f3;
}
</style>
